import { PrincipalDocumentScope, PrincipalType } from '~/swagger/companies/Api';
import { createPrincipalClientKey } from '~/utils/companyBusinessPrincipalClientKey';
import { revokeLocalHydrateFileUrls } from '~/utils/uploaderAdapter';
import type { HydrateFile } from '@magicdoorinc/ui';
import type {
  BusinessInfoDraftDocumentDto,
  BusinessInfoDraftDto,
  BusinessInfoDraftPrincipalDto,
  BusinessInfoRequirementDto,
  PrincipalDocumentObligationDto,
} from '~/swagger/companies/Api';
import type {
  BusinessInfoFileFormValue,
  CompanyBusinessInfoFormPayload,
  PrincipalFileFormValue,
  PrincipalFormValue,
} from '~/utils/companyBusinessInfoPayload';

export type NamedDocument = {
  obligationKey: string;
  documentId?: string;
  fileId: string;
  principalId?: string | null;
  name: string;
};

export type BusinessObligationConfig = {
  obligationKey: string;
  label: string;
  minCount: number;
  maxCount?: number;
  description?: string;
};

export type PrincipalObligationConfig = BusinessObligationConfig & {
  principalScope: PrincipalDocumentScope;
  ownershipThresholdPercent?: number;
};

export type BusinessInfoHydrateFile = HydrateFile & {
  documentId?: string;
  fileIdRedacted?: boolean;
};

const REDACTED_FILE_PREFIX = 'document:';

export const isRedactedDraftFile = (file: HydrateFile): boolean => {
  const businessFile = file as BusinessInfoHydrateFile;
  return Boolean(businessFile.fileIdRedacted) || file.idOrUrl.startsWith(REDACTED_FILE_PREFIX);
};

export const mapDraftDocumentToHydrateFile = (
  doc: BusinessInfoDraftDocumentDto,
  fallbackName: string,
  displayName?: string
): BusinessInfoHydrateFile => {
  const hasFileId = Boolean(doc.fileId?.trim());

  return {
    idOrUrl: hasFileId ? doc.fileId : `${REDACTED_FILE_PREFIX}${doc.id}`,
    name: displayName ?? doc.description ?? fallbackName,
    documentId: doc.id,
    fileIdRedacted: !hasFileId,
    uploaded: 'uploaded' as HydrateFile['uploaded'],
  };
};

const buildDocumentDisplayName = (doc: BusinessInfoDraftDocumentDto, fallbackName: string, index: number, total: number) => {
  const trimmedDescription = doc.description?.trim();
  const baseName = trimmedDescription ? trimmedDescription : fallbackName;
  if (total <= 1) return baseName;
  return `${baseName} (${index + 1})`;
};

export const buildNamedDocuments = (docs: BusinessInfoDraftDocumentDto[], requirement?: BusinessInfoRequirementDto): NamedDocument[] => {
  const obligationLabels = new Map<string, string>();
  requirement?.businessDocuments?.forEach((item) => obligationLabels.set(item.key, item.label));
  requirement?.principalDocuments?.forEach((item) => obligationLabels.set(item.key, item.label));

  return docs.map((doc) => ({
    obligationKey: doc.obligationKey,
    documentId: doc.id,
    fileId: doc.fileId,
    principalId: doc.principalId,
    name: obligationLabels.get(doc.obligationKey) ?? doc.description ?? doc.obligationKey,
  }));
};

export const getBusinessObligationConfigs = (requirement?: BusinessInfoRequirementDto): BusinessObligationConfig[] => {
  return (requirement?.businessDocuments ?? []).map((item) => ({
    obligationKey: item.key,
    label: item.label,
    minCount: item.minCount,
    maxCount: item.maxCount,
    description: item.description,
  }));
};

export const getPrincipalObligationConfigsForPrincipal = (
  requirement?: BusinessInfoRequirementDto,
  principal?: Pick<BusinessInfoDraftPrincipalDto, 'type' | 'ownership'>
): PrincipalObligationConfig[] => {
  const obligations = requirement?.principalDocuments ?? [];
  return obligations
    .filter((obligation) => matchesPrincipalScope(obligation, principal))
    .map((item) => ({
      obligationKey: item.key,
      label: item.label,
      minCount: item.minCount,
      maxCount: item.maxCount,
      description: item.description,
      principalScope: item.principalScope,
      ownershipThresholdPercent: item.ownershipThresholdPercent,
    }));
};

const matchesPrincipalScope = (
  obligation: PrincipalDocumentObligationDto,
  principal?: Pick<BusinessInfoDraftPrincipalDto, 'type' | 'ownership'>
): boolean => {
  if (!principal) return obligation.principalScope === PrincipalDocumentScope.AllPrincipals;

  switch (obligation.principalScope) {
    case PrincipalDocumentScope.AllPrincipals:
      return true;
    case PrincipalDocumentScope.ControlPerson:
      return principal.type === PrincipalType.ControlPerson;
    case PrincipalDocumentScope.BeneficialOwners:
      const threshold = obligation.ownershipThresholdPercent ?? 25;
      return (principal.ownership ?? 0) >= threshold;
    default:
      return false;
  }
};

export const getEmptyBusinessInfoFileValues = (requirement?: BusinessInfoRequirementDto): BusinessInfoFileFormValue[] => {
  return getBusinessObligationConfigs(requirement).map((item) => ({
    obligationKey: item.obligationKey,
    label: item.label,
    description: item.description,
    files: undefined,
  }));
};

export const getEmptyPrincipalFileValues = (
  requirement?: BusinessInfoRequirementDto,
  principal?: Pick<BusinessInfoDraftPrincipalDto, 'type' | 'ownership'>
): PrincipalFileFormValue[] => {
  return getPrincipalObligationConfigsForPrincipal(requirement, principal).map((item) => ({
    obligationKey: item.obligationKey,
    label: item.label,
    description: item.description,
    files: undefined,
  }));
};

export const getBusinessInfoFileValuesFromDocuments = (
  requirement?: BusinessInfoRequirementDto,
  docs: BusinessInfoDraftDocumentDto[] = []
): BusinessInfoFileFormValue[] => {
  const obligations = getBusinessObligationConfigs(requirement);
  if (!obligations.length) return [];

  return obligations.map((obligation) => {
    const matchingDocs = docs.filter((document) => !document.principalId && document.obligationKey === obligation.obligationKey);

    return {
      obligationKey: obligation.obligationKey,
      label: obligation.label,
      description: obligation.description,
      files:
        matchingDocs.length > 0
          ? matchingDocs
              .filter((doc) => doc.id)
              .map((doc, index) =>
                mapDraftDocumentToHydrateFile(
                  doc,
                  obligation.label,
                  buildDocumentDisplayName(doc, obligation.label, index, matchingDocs.length)
                )
              )
          : undefined,
    };
  });
};

export const getDocumentDefaultFileList = (
  namedDocuments: NamedDocument[],
  obligationKey: string,
  principalId?: string
): HydrateFile[] | undefined => {
  const matchedDocuments = namedDocuments.filter((document) => {
    if (!document.documentId) return false;
    if (principalId) {
      return document.obligationKey === obligationKey && document.principalId === principalId;
    }
    return document.obligationKey === obligationKey && !document.principalId;
  });

  if (!matchedDocuments.length) return undefined;

  return matchedDocuments.map((document, index) =>
    mapDraftDocumentToHydrateFile(
      {
        id: document.documentId,
        obligationKey: document.obligationKey,
        fileId: document.fileId,
        principalId: document.principalId ?? undefined,
        created: '',
      },
      document.name,
      buildDocumentDisplayName(
        {
          obligationKey: document.obligationKey,
          fileId: document.fileId,
          description: document.name,
          created: '',
        },
        document.name,
        index,
        matchedDocuments.length
      )
    )
  );
};

export const getPrincipalFormValuesFromDraft = (
  principals: BusinessInfoDraftPrincipalDto[],
  requirement?: BusinessInfoRequirementDto,
  docs: BusinessInfoDraftDocumentDto[] = []
): PrincipalFormValue[] => {
  return principals.map((principal) => {
    const obligations = getPrincipalObligationConfigsForPrincipal(requirement, principal);
    return {
      id: principal.id,
      clientKey: createPrincipalClientKey(principal.id),
      firstName: principal.firstName,
      lastName: principal.lastName,
      email: principal.email,
      phone: principal.phone,
      ssn: principal.ssn,
      dateOfBirth: principal.dateOfBirth,
      type: principal.type,
      ownership: principal.ownership,
      address: principal.address,
      driversLicense: {
        number: principal.driverLicense?.number ?? '',
        state: principal.driverLicense?.state ?? '',
      },
      principalFiles: obligations.map((obligation) => {
        const matchingDocs = docs.filter(
          (document) => document.principalId === principal.id && document.obligationKey === obligation.obligationKey && document.id
        );

        return {
          obligationKey: obligation.obligationKey,
          label: obligation.label,
          description: obligation.description,
          files:
            matchingDocs.length > 0
              ? matchingDocs.map((doc, index) =>
                  mapDraftDocumentToHydrateFile(
                    doc,
                    obligation.label,
                    buildDocumentDisplayName(doc, obligation.label, index, matchingDocs.length)
                  )
                )
              : undefined,
        };
      }),
    };
  });
};

const revokeFormDocumentFileUrls = (formStore: { businessInfoFiles?: BusinessInfoFileFormValue[]; principals?: PrincipalFormValue[] }) => {
  formStore.businessInfoFiles?.forEach(({ files }) => revokeLocalHydrateFileUrls(files));
  formStore.principals?.forEach((principal) => {
    principal.principalFiles?.forEach(({ files }) => revokeLocalHydrateFileUrls(files));
  });
};

const matchDraftPrincipal = (
  principal: PrincipalFormValue,
  index: number,
  draftPrincipals: BusinessInfoDraftPrincipalDto[]
): BusinessInfoDraftPrincipalDto | undefined => {
  if (principal.id) {
    const byId = draftPrincipals.find((entry) => entry.id === principal.id);
    if (byId) return byId;
  }
  return draftPrincipals[index];
};

export const mergeFormDocumentFieldsFromDraft = (
  formStore: { businessInfoFiles?: BusinessInfoFileFormValue[]; principals?: PrincipalFormValue[] },
  draft: BusinessInfoDraftDto,
  requirement?: BusinessInfoRequirementDto
): Pick<CompanyBusinessInfoFormPayload, 'businessInfoFiles' | 'principals'> => {
  revokeFormDocumentFileUrls(formStore);

  const businessInfoFiles = getBusinessInfoFileValuesFromDocuments(requirement, draft.documents);
  const formPrincipals = formStore.principals ?? [];

  const principals = formPrincipals.map((principal, index) => {
    const draftPrincipal = matchDraftPrincipal(principal, index, draft.principals);
    const principalFiles = draftPrincipal
      ? getPrincipalFormValuesFromDraft([draftPrincipal], requirement, draft.documents)[0]?.principalFiles
      : principal.principalFiles;

    return {
      ...principal,
      clientKey: principal.clientKey ?? createPrincipalClientKey(draftPrincipal?.id),
      id: draftPrincipal?.id ?? principal.id,
      principalFiles,
    };
  });

  return { businessInfoFiles, principals };
};
